import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { RefreshCw, Check, AlertCircle } from 'lucide-react';
import { useAppTranslation } from '../hooks/useAppTranslation.js';
import { PasswordInput } from '../components/PasswordInput.js';

interface InvitationData {
  email: string;
  role: string;
  organisationName: string;
}

export default function InvitationAcceptancePage() {
  const { t } = useAppTranslation();
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const [invitation, setInvitation] = useState<InvitationData | null>(null);
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [passwordError, setPasswordError] = useState<string | null>(null);
  const [hasAccepted, setHasAccepted] = useState(false);

  useEffect(() => {
    if (hasAccepted || !token) return;

    const fetchInvitation = async () => {
      try {
        const res = await fetch(`/api/invitations/${token}`, {
          method: 'GET',
          credentials: 'include',
        });
        if (res.ok) {
          const data = await res.json();
          setInvitation(data);
          setError(null);
        } else {
          const errorData = await res.json().catch(() => ({}));
          if (errorData.error && errorData.error.includes('already been accepted')) {
            setTimeout(() => navigate('/'), 500);
          } else {
            setError(errorData.error || t('invitation.errors.notFound', 'Invitation not found or has expired'));
            setInvitation(null);
          }
        }
      } catch {
        setError(t('invitation.errors.loadError', 'Error loading invitation'));
        setInvitation(null);
      } finally {
        setIsLoading(false);
      }
    };

    fetchInvitation();
  }, [token, hasAccepted, navigate, t]);

  const validatePasswords = () => {
    setPasswordError(null);
    if (!password) {
      setPasswordError(t('invitation.validation.passwordRequired', 'Password is required'));
      return false;
    }
    if (password.length < 8) {
      setPasswordError(t('invitation.validation.passwordMinLength', 'Password must be at least 8 characters'));
      return false;
    }
    if (password !== confirmPassword) {
      setPasswordError(t('invitation.validation.passwordMismatch', 'Passwords do not match'));
      return false;
    }
    return true;
  };

  const handleAcceptInvitation = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validatePasswords()) return;

    setIsSubmitting(true);
    try {
      const res = await fetch(`/api/invitations/${token}/accept`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ password }),
      });

      if (res.ok) {
        setHasAccepted(true);
        setTimeout(() => navigate('/'), 1000);
      } else {
        const errorData = await res.json().catch(() => ({}));
        setPasswordError(errorData.error || t('invitation.errors.acceptFailed', 'Failed to accept invitation'));
      }
    } catch {
      setPasswordError(t('invitation.errors.acceptError', 'Error accepting invitation'));
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-main dark:bg-main-dark">
        <div className="text-center">
          <RefreshCw className="w-8 h-8 animate-spin text-action dark:text-action-dark mx-auto mb-4" />
          <p className="text-text-primary dark:text-text-primary-dark">
            {t('invitation.loading', 'Loading invitation...')}
          </p>
        </div>
      </div>
    );
  }

  if (error || !invitation) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-main dark:bg-main-dark p-4">
        <div className="max-w-md w-full bg-card dark:bg-card-dark rounded-2xl shadow-lg p-8 text-center">
          <AlertCircle className="w-12 h-12 text-danger-500 mx-auto mb-4" />
          <h1 className="text-xl font-bold text-text-primary dark:text-text-primary-dark mb-2">
            {t('invitation.invalidTitle', 'Invitation Invalid')}
          </h1>
          <p className="text-text-secondary dark:text-text-secondary-dark mb-6">
            {error || t('invitation.invalidMessage', 'This invitation link is not valid or has expired.')}
          </p>
          <button
            onClick={() => navigate('/login')}
            className="w-full px-4 py-2.5 bg-accent-600 text-white rounded-lg font-medium hover:bg-accent-500"
          >
            {t('invitation.actions.goToLogin', 'Go to Login')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex items-center justify-center min-h-screen bg-main dark:bg-main-dark p-4">
      <div className="max-w-md w-full">
        <div className="bg-card dark:bg-card-dark rounded-2xl shadow-lg p-8 mb-4">
          <h1 className="text-2xl font-bold text-text-primary dark:text-text-primary-dark mb-2">
            {t('invitation.joinTitle', 'Join {{organisationName}}', {
              organisationName: invitation.organisationName,
            })}
          </h1>
          <p className="text-text-secondary dark:text-text-secondary-dark mb-6">
            {t("invitation.joinSubtitle", "You've been invited to join as a {{role}}", {
              role: invitation.role,
            })}
          </p>

          <form onSubmit={handleAcceptInvitation} className="space-y-4">
            <div>
              <label className="text-sm font-medium text-text-primary dark:text-text-secondary-dark block mb-2">
                {t('common.fields.email', 'Email')}
              </label>
              <input
                type="email"
                value={invitation.email}
                disabled
                className="w-full px-4 py-2.5 rounded-lg border border-border dark:border-border-dark bg-surface dark:bg-surface-dark text-text-secondary dark:text-text-secondary-dark"
              />
            </div>

            <div>
              <label className="text-sm font-medium text-text-primary dark:text-text-secondary-dark block mb-2">
                {t('invitation.fields.password', 'Password')}
              </label>
              <PasswordInput
                value={password}
                onChange={(e) => {
                  setPassword(e.target.value);
                  setPasswordError(null);
                }}
                placeholder={t('invitation.fields.passwordPlaceholder', 'Enter password (min 8 characters)')}
                className="w-full px-4 py-2.5 rounded-lg border border-border dark:border-border-dark bg-input-bg dark:bg-input-bg-dark text-text-primary dark:text-text-primary-dark focus:outline-none focus:ring-2"
              />
            </div>

            <div>
              <label className="text-sm font-medium text-text-primary dark:text-text-secondary-dark block mb-2">
                {t('invitation.fields.confirmPassword', 'Confirm Password')}
              </label>
              <PasswordInput
                value={confirmPassword}
                onChange={(e) => {
                  setConfirmPassword(e.target.value);
                  setPasswordError(null);
                }}
                placeholder={t('invitation.fields.confirmPasswordPlaceholder', 'Confirm password')}
                className="w-full px-4 py-2.5 rounded-lg border border-border dark:border-border-dark bg-input-bg dark:bg-input-bg-dark text-text-primary dark:text-text-primary-dark focus:outline-none focus:ring-2"
              />
            </div>

            {passwordError && (
              <div className="p-3 bg-danger-100 dark:bg-danger-900/30 border border-danger-300 dark:border-danger-700 rounded-lg text-danger-700 dark:text-danger-200 text-sm">
                {passwordError}
              </div>
            )}

            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full px-4 py-2.5 bg-accent-600 text-white rounded-lg font-bold hover:bg-accent-500 disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {isSubmitting ? (
                <>
                  <RefreshCw className="w-4 h-4 animate-spin" />
                  {t('invitation.states.creatingAccount', 'Creating Account...')}
                </>
              ) : (
                <>
                  <Check className="w-4 h-4" />
                  {t('invitation.actions.accept', 'Accept Invitation')}
                </>
              )}
            </button>
          </form>

          <p className="text-xs text-text-muted dark:text-text-muted-dark text-center mt-4">
            {t('invitation.footerAgreement', 'By accepting this invitation, you agree to join the organization')}
          </p>
        </div>
      </div>
    </div>
  );
}
